import { VISUAL_REGISTRY } from '../../../../../visuals'
import { QUESTION_TYPE_REGISTRY } from '../../../../../content'
import { VISUAL_CATEGORIES, TYPE_CATEGORIES } from './categoryDefinitions'

/**
 * Coverage report between a runtime registry (source of truth) and its
 * hard-coded category-mapping (the admin explorer's UX layout).
 *
 * - `uncategorised`: ids present in the registry but NOT mapped to any
 *   category. These will only ever surface in the explorer's
 *   "Overige Actieve…" fallback bucket — a sign the category-mapping has
 *   drifted from the registry.
 * - `phantom`: ids referenced in a category but NOT present in the
 *   registry. These render as empty slots in the explorer — usually means
 *   a visual/type was renamed or removed without updating the mapping.
 */
export interface CategoryCoverageReport {
  registrySize: number
  categorisedCount: number
  uncategorised: string[]
  phantom: { categoryId: string; itemId: string }[]
}

function buildReport(
  registryKeys: string[],
  categories: { id: string; items: string[] }[],
): CategoryCoverageReport {
  const registrySet = new Set(registryKeys)
  const mappedIds = new Set<string>()
  const phantom: { categoryId: string; itemId: string }[] = []

  for (const cat of categories) {
    for (const itemId of cat.items) {
      mappedIds.add(itemId)
      if (!registrySet.has(itemId)) {
        phantom.push({ categoryId: cat.id, itemId })
      }
    }
  }

  const uncategorised = registryKeys.filter((k) => !mappedIds.has(k)).sort()

  // Count items that are both in the registry and mapped to a category.
  const categorisedCount = registryKeys.filter((k) => mappedIds.has(k)).length

  return {
    registrySize: registryKeys.length,
    categorisedCount,
    uncategorised,
    phantom: phantom.sort((a, b) =>
      a.categoryId === b.categoryId
        ? a.itemId.localeCompare(b.itemId)
        : a.categoryId.localeCompare(b.categoryId),
    ),
  }
}

export function getVisualCategoryCoverage(): CategoryCoverageReport {
  return buildReport(Object.keys(VISUAL_REGISTRY), VISUAL_CATEGORIES)
}

export function getTypeCategoryCoverage(): CategoryCoverageReport {
  return buildReport(Object.keys(QUESTION_TYPE_REGISTRY), TYPE_CATEGORIES)
}

/**
 * True if either registry has uncategorised entries or phantom mappings.
 * Useful for surfacing a single "needs attention" badge.
 */
export function hasCategoryDrift(): boolean {
  const v = getVisualCategoryCoverage()
  const t = getTypeCategoryCoverage()
  return (
    v.uncategorised.length > 0 ||
    v.phantom.length > 0 ||
    t.uncategorised.length > 0 ||
    t.phantom.length > 0
  )
}
